/**
 * Explosions API client (Phase 3c Step 8).
 * Uses relative `/api` — Vite proxy injects X-Api-Token server-side.
 */

import type {
  BomVersionUsed,
  ExplosionRun,
  ExplosionRunDetail,
  RunExplosionInput,
} from './types'

export type ExplosionsApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class ExplosionsApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'ExplosionsApiClientError'
    this.code = code
    this.status = status
  }
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as ExplosionsApiError
  throw new ExplosionsApiClientError(
    err.error?.code ?? 'HTTP_ERROR',
    err.error?.message ?? `${fallback} (${res.status})`,
    err.error?.status ?? res.status,
  )
}

function normalizeRun(run: ExplosionRunDetail | ExplosionRun): ExplosionRun {
  const used = run.bom_versions_used
  return {
    ...run,
    bom_versions_used: Array.isArray(used) ? (used as BomVersionUsed[]) : used,
  }
}

function normalizeRunDetail(run: ExplosionRunDetail): ExplosionRunDetail {
  return { ...normalizeRun(run), results: run.results }
}

export async function listRunsApi(): Promise<ExplosionRun[]> {
  const res = await fetch('/api/explosions/runs')
  const body = await parseJson<
    { ok: true; runs: ExplosionRun[] } | ExplosionsApiError
  >(res)
  throwIfApiError(res, body, 'Failed to list explosion runs')
  return (body as { runs: ExplosionRun[] }).runs.map(normalizeRun)
}

export async function getRunApi(
  id: string,
): Promise<ExplosionRunDetail | null> {
  const res = await fetch(`/api/explosions/runs/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<
    { ok: true; run: ExplosionRunDetail } | ExplosionsApiError
  >(res)
  throwIfApiError(res, body, 'Failed to get explosion run')
  return normalizeRunDetail((body as { run: ExplosionRunDetail }).run)
}

export async function runExplosionApi(
  input: RunExplosionInput,
): Promise<ExplosionRunDetail> {
  const res = await fetch('/api/explosions/runs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const body = await parseJson<
    { ok: true; run: ExplosionRunDetail } | ExplosionsApiError
  >(res)
  throwIfApiError(res, body, 'Chạy Gross Explosion thất bại')
  return normalizeRunDetail((body as { run: ExplosionRunDetail }).run)
}
